import { useRef, useLayoutEffect, useState } from "react";
import { useConfirmDialog } from "@hooks";
import { YesNoCancelDialogOptions } from "@containers";
import locale from "@bonliva-crm/locale";
import {
  ConsultantQuality_GetConsultantNew,
  ConsultantEventLog_GetEvents,
  useRequestHealthDeclarationViaOneflowNewMutation
} from "@graphql";
import WithTooltip from "@common/WithTooltip";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography
} from "@material-ui/core";
import Button from "@material-ui/core/Button";
import { useSnackbarsPipeline } from "@bonliva-crm/ui";
import { QualityCheckButton } from "../quality-check-button";
import { QualityCheck, QualityCheckProps } from "../quality-check";
import { HealthDeclarationList } from "../../health-declaration-list";
import { QualityCheckMeta } from "../quality-check-meta";
import {
  isPartiallyExpired,
  getTransitionParams,
  isDisabledByStatus,
  isSendAllowed,
  getRequestSentTitle,
  getLastFullHDStatus,
  mrsaDisabled,
  fullHDDisabled,
  isNowHalfYearAfterSetOn
} from "./utils";
import { LabelWithInfo } from "@consultants/consultant-profile-new/quality-checks/components/quality-check/label-with-info";
import { formatLabelDate } from "@consultants/consultant-profile-new/quality-checks/components/quality-check/utils";

export function HealthDeclarationQualityCheck(props: QualityCheckProps) {
  const { data, consultantData } = props;

  if (data.Name !== "Hälsodeklaration") {
    throw new Error("Wrong quality check");
  }

  const anchorRef = useRef(null);
  const [showDocumentList, setShowDocumentList] = useState(false);

  const [sendRequest, sendRequestResult] =
    useRequestHealthDeclarationViaOneflowNewMutation({
      refetchQueries: [
        ConsultantQuality_GetConsultantNew.OperationName,
        ConsultantEventLog_GetEvents.OperationName
      ]
    });

  const snackbar = useSnackbarsPipeline();

  useLayoutEffect(() => {
    if (!anchorRef.current) {
      anchorRef.current = document.getElementById(`health_check_view_${props.data._id}`);
    }
  }, [props.data._id]);

  const confirmDialog = useConfirmDialog();

  const halfYearPassed = isNowHalfYearAfterSetOn(data);
  const disabledMRSA = mrsaDisabled(data, halfYearPassed);
  const disabledFullHD = fullHDDisabled(data, halfYearPassed);

  const handleSend = () => {
    const options = new YesNoCancelDialogOptions("Skicka hälsodeklaration");
    options.message =
      "Välj nedan om du vill skicka en fullständig hälsodeklaration till konsulten eller om du enbart vill skicka MRSA-delen.";
    options.actions[1].title = "MRSA";
    options.actions[1].value = "MRSA" as any;
    options.actions[1].disabled = disabledMRSA;
    options.actions[2].title = "HD";
    options.actions[2].value = "HD" as any;
    options.actions[2].disabled = disabledFullHD;

    confirmDialog
      .confirm(options)
      .then(async (value) => {
        if (value === "MRSA") {
          await sendRequest({
            variables: {
              consultantId: consultantData._id,
              qualityTemplate: data.templateName,
              transitionParams: getTransitionParams(data, false)
            }
          });
        }
        if (value === "HD") {
          await sendRequest({
            variables: {
              consultantId: consultantData._id,
              qualityTemplate: data.templateName,
              transitionParams: getTransitionParams(data, true)
            }
          });
        }
        if (value !== "cancel") {
          snackbar.showSuccess("Utskicket har levererats.");
        }
      })
      .catch(() => {
        snackbar.showError("Ditt utskick kunde inte levereras.");
      });
  };

  const toggleDocumentList = () => {
    setShowDocumentList(!showDocumentList);
  };

  const sendAllowed = isSendAllowed(consultantData);
  const statusComment = data.Status?.Comment;

  const disabledByStatus = isDisabledByStatus(data);
  const disabled =
    sendRequestResult.loading ||
    !sendAllowed ||
    disabledByStatus ||
    (disabledMRSA && disabledFullHD);

  return (
    <QualityCheck
      {...props}
      showComment
      withDefaultStatuses={false}
      renderLabel={(label) => (
        <LabelWithInfo
          label={label}
          info={
            "Det går inte att skicka en hälsodeklaration om konsultprofilen saknar Ansvarig HBA eller om status för Hälsodeklarationen redan är “Förfrågan skickad”."
          }
        />
      )}
      renderExtraAction={() => {
        const button = (
          <QualityCheckButton
            text="Skicka"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleSend();
            }}
            disabled={disabled}
            loading={sendRequestResult.loading}
          />
        );

        if (!sendAllowed || disabledByStatus) {
          return (
            <WithTooltip
              title={
                disabledByStatus
                  ? "Du kan inte skicka en ny hälsodeklaration medans ett flöde är aktivt."
                  : "Konsulten måste ha ett giltigt personnummer, ansvarig HBA, e-post och telefonnummer för att kunna skicka en hälsodeklaration."
              }
              placement="top"
            >
              <span>{button}</span>
            </WithTooltip>
          );
        }

        return button;
      }}
      renderExtraLabels={() => {
        const anchorId = `health_check_view_${props.data._id}`;

        const statusSetOn = data.StatusSetOn
          ? `${formatLabelDate(data.StatusSetOn)}`
          : null;

        const expiresOn = data.ExpiresOn ? `${formatLabelDate(data.ExpiresOn)}` : null;

        const isHalfExpired = isPartiallyExpired(
          data.Name,
          data.Status,
          data.HasExpired,
          data.StatusSetOn
        );
        const lastFullHDStatus = getLastFullHDStatus(data);

        return (
          <>
            {data.StatusSetOn && (
              <QualityCheckMeta
                title={getRequestSentTitle(
                  locale.qualityStatuses[data.Status.Name],
                  data
                )}
                description={data.StatusSetBy.Name}
              />
            )}

            {statusSetOn && (
              <QualityCheckMeta title={locale.Date} description={statusSetOn} />
            )}

            {statusComment ? (
              <QualityCheckMeta title={locale.Comment} description={statusComment} />
            ) : null}

            {expiresOn && (
              <QualityCheckMeta title={"Förfaller"} description={expiresOn} />
            )}

            {isHalfExpired && (
              <Typography variant={"caption"} color={"error"}>
                MRSA-frågor har förfallit - skicka ny HD om konsult blir aktuell för
                uppdrag
              </Typography>
            )}

            {lastFullHDStatus && (
              <QualityCheckMeta title={"HD-status"} description={lastFullHDStatus} />
            )}

            <Box id={anchorId} mt={2} textAlign="right">
              <Button
                size="small"
                variant="outlined"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  toggleDocumentList();
                }}
              >
                <span>Visa i OneFlow…</span>
              </Button>
            </Box>

            <Dialog
              open={showDocumentList}
              maxWidth="sm"
              fullWidth
              onClose={() => toggleDocumentList()}
            >
              <DialogTitle>Health Declarations</DialogTitle>

              <DialogContent>
                <HealthDeclarationList consultantId={props.consultantData._id} />
              </DialogContent>

              <DialogActions>
                <Button onClick={() => toggleDocumentList()}>{locale.Close}</Button>
              </DialogActions>
            </Dialog>
          </>
        );
      }}
    />
  );
}
